import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Calendar,
  Clock,
  Shield,
  Key,
  Save,
  Loader2,
  Activity,
  Hash,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function MyProfile() {
  const { user, profile, userRole, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    phone: "",
    position: "",
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  // Cargar datos del perfil
  const { data: profileData, isLoading } = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await api
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Cargar actividad reciente
  const { data: recentActivity } = useQuery({
    queryKey: ["my-activity", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await api
        .from("audit_logs")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Mutación para actualizar perfil
  const updateProfile = useMutation({
    mutationFn: async (data: typeof formData) => {
      const { error } = await api
        .from("profiles")
        .update({
          full_name: data.full_name,
          phone: data.phone || null,
          position: data.position || null,
        })
        .eq("id", user?.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      refreshProfile();
      toast.success("Perfil actualizado correctamente");
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al actualizar perfil");
    },
  });

  // Mutación para cambiar contraseña
  const changePassword = useMutation({
    mutationFn: async (data: typeof passwordData) => {
      // Verificar contraseña actual
      const verifyResponse = await api.rpc("verify_password", {
        user_id: user?.id,
        password: data.current_password,
      });

      if (!verifyResponse.data) {
        throw new Error("La contraseña actual es incorrecta");
      }

      // Actualizar contraseña (hash en el backend)
      const { error } = await api
        .from("users")
        .update({
          password_hash: data.new_password, // El trigger o función debería hashear esto
        })
        .eq("id", user?.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Contraseña actualizada correctamente");
      setPasswordDialogOpen(false);
      setPasswordData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al cambiar contraseña");
    },
  });

  const handleEditStart = () => {
    setFormData({
      full_name: profileData?.full_name || "",
      phone: profileData?.phone || "",
      position: profileData?.position || "",
    });
    setIsEditing(true);
  };

  const handleSave = () => {
    if (!formData.full_name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    updateProfile.mutate(formData);
  };

  const handlePasswordChange = () => {
    if (!passwordData.current_password) {
      toast.error("Ingresa tu contraseña actual");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    changePassword.mutate(passwordData);
  };

  const getRoleBadge = (role: string | null) => {
    const config: Record<string, { label: string; color: string }> = {
      admin: { label: "Administrador", color: "bg-red-500/10 text-red-500" },
      supervisor: { label: "Supervisor", color: "bg-purple-500/10 text-purple-500" },
      operator: { label: "Operador", color: "bg-blue-500/10 text-blue-500" },
      quality: { label: "Calidad", color: "bg-green-500/10 text-green-500" },
      warehouse: { label: "Almacén", color: "bg-orange-500/10 text-orange-500" },
      maintenance: { label: "Mantenimiento", color: "bg-cyan-500/10 text-cyan-500" },
      viewer: { label: "Visor", color: "bg-gray-500/10 text-gray-500" },
    };
    const { label, color } = config[role || "viewer"] || config.viewer;
    return <Badge className={color}>{label}</Badge>;
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return "Nunca";
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: es });
  };

  const getOperationLabel = (operation: string) => {
    const labels: Record<string, string> = {
      INSERT: "Creó",
      UPDATE: "Actualizó",
      DELETE: "Eliminó",
    };
    return labels[operation] || operation;
  };

  const getTableLabel = (table: string) => {
    const labels: Record<string, string> = {
      work_orders: "orden de trabajo",
      profiles: "perfil",
      molds: "molde",
      machines: "máquina",
      customers: "cliente",
      process_records: "registro de proceso",
    };
    return labels[table] || table;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Mi Perfil</h1>
            <p className="text-muted-foreground mt-1">
              Gestiona tu información personal y preferencias
            </p>
          </div>
          {!isEditing ? (
            <Button onClick={handleEditStart}>Editar Perfil</Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={updateProfile.isPending}>
                {updateProfile.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar
              </Button>
            </div>
          )}
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="profile">
              <User className="w-4 h-4 mr-2" />
              Perfil
            </TabsTrigger>
            <TabsTrigger value="security">
              <Key className="w-4 h-4 mr-2" />
              Seguridad
            </TabsTrigger>
            <TabsTrigger value="activity">
              <Activity className="w-4 h-4 mr-2" />
              Actividad
            </TabsTrigger>
          </TabsList>

          {/* Tab: Perfil */}
          <TabsContent value="profile" className="mt-6 space-y-6">
            {/* Info principal */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-6">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-3xl font-bold text-primary">
                      {(profileData?.full_name || user?.email)?.[0]?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 space-y-4">
                    {isEditing ? (
                      <div className="space-y-2">
                        <Label>Nombre Completo</Label>
                        <Input
                          value={formData.full_name}
                          onChange={(e) =>
                            setFormData({ ...formData, full_name: e.target.value })
                          }
                          placeholder="Tu nombre completo"
                        />
                      </div>
                    ) : (
                      <>
                        <h2 className="text-2xl font-semibold">
                          {profileData?.full_name || "Sin nombre"}
                        </h2>
                        <p className="text-muted-foreground">{user?.email}</p>
                        <div className="flex items-center gap-2">
                          {getRoleBadge(userRole)}
                          <Badge variant="outline" className="bg-green-500/10 text-green-500">
                            Activo
                          </Badge>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Información de contacto */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Mail className="w-5 h-5" />
                  Información de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-sm">Email</Label>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Label>Teléfono</Label>
                        <Input
                          value={formData.phone}
                          onChange={(e) =>
                            setFormData({ ...formData, phone: e.target.value })
                          }
                          placeholder="+34 600 000 000"
                        />
                      </div>
                    ) : (
                      <>
                        <Label className="text-muted-foreground text-sm">Teléfono</Label>
                        <p className="font-medium">{profileData?.phone || "-"}</p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Información laboral */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Briefcase className="w-5 h-5" />
                  Información Laboral
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground text-sm">ID Empleado</Label>
                    <p className="font-medium">{profileData?.employee_id || "-"}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Departamento</Label>
                    <p className="font-medium capitalize">{profileData?.department || "-"}</p>
                  </div>
                  <div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <Label>Puesto</Label>
                        <Input
                          value={formData.position}
                          onChange={(e) =>
                            setFormData({ ...formData, position: e.target.value })
                          }
                          placeholder="Tu puesto de trabajo"
                        />
                      </div>
                    ) : (
                      <>
                        <Label className="text-muted-foreground text-sm">Puesto</Label>
                        <p className="font-medium">{profileData?.position || "-"}</p>
                      </>
                    )}
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-sm">Área</Label>
                    <p className="font-medium capitalize">{profileData?.area || "-"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Estadísticas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="w-5 h-5" />
                  Estadísticas de Cuenta
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <Calendar className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Fecha de Alta</p>
                    <p className="font-semibold">
                      {profileData?.hire_date
                        ? format(new Date(profileData.hire_date), "dd/MM/yyyy", { locale: es })
                        : "-"}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <Clock className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Último Acceso</p>
                    <p className="font-semibold text-sm">
                      {formatDateTime(profileData?.last_login)}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-muted/50">
                    <Hash className="w-5 h-5 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Total Accesos</p>
                    <p className="font-semibold">{profileData?.login_count || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Seguridad */}
          <TabsContent value="security" className="mt-6 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Key className="w-5 h-5" />
                  Contraseña
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-4">
                  Cambia tu contraseña periódicamente para mantener tu cuenta segura.
                </p>
                <Button onClick={() => setPasswordDialogOpen(true)}>
                  <Key className="w-4 h-4 mr-2" />
                  Cambiar Contraseña
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5" />
                  Rol y Permisos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  {getRoleBadge(userRole)}
                  <p className="text-muted-foreground">
                    Tu rol determina qué acciones puedes realizar en el sistema.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Tab: Actividad */}
          <TabsContent value="activity" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Activity className="w-5 h-5" />
                  Actividad Reciente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentActivity && recentActivity.length > 0 ? (
                  <div className="space-y-4">
                    {recentActivity.map((activity: any) => (
                      <div
                        key={activity.id}
                        className="flex items-start gap-4 p-3 rounded-lg border"
                      >
                        <div className="p-2 rounded-full bg-primary/10">
                          <Activity className="w-4 h-4 text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">
                            {getOperationLabel(activity.operation)}{" "}
                            {getTableLabel(activity.table_name)}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {formatDateTime(activity.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">
                    No hay actividad reciente
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialog: Cambiar Contraseña */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" />
              Cambiar Contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Contraseña Actual</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, current_password: e.target.value })
                }
                placeholder="••••••••"
              />
            </div>
            <Separator />
            <div className="space-y-2">
              <Label>Nueva Contraseña</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, new_password: e.target.value })
                }
                placeholder="••••••••"
              />
              <p className="text-xs text-muted-foreground">
                Mínimo 6 caracteres, incluir mayúscula y número
              </p>
            </div>
            <div className="space-y-2">
              <Label>Confirmar Nueva Contraseña</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) =>
                  setPasswordData({ ...passwordData, confirm_password: e.target.value })
                }
                placeholder="••••••••"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handlePasswordChange} disabled={changePassword.isPending}>
              {changePassword.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Key className="w-4 h-4 mr-2" />
              )}
              Cambiar Contraseña
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}





